import ReactGA from "react-ga4";
import { TutorialEventHandler, TutorialEventPayload } from "./types";

// GA4 event category used by every tutorial event. Kept as a constant so
// dashboards can group events consistently (spec 8 章 の QA verify で
// hitType='event' category='tutorial' を確認する場合の期待値と一致)。
export const TUTORIAL_GA4_CATEGORY = "tutorial";

// Map internal event names to the GA4 action string. Kept 1:1 with the spec
// so upstream dashboards can filter by action without transformation.
const ACTION_BY_EVENT: Record<TutorialEventPayload["event"], string> = {
  tutorial_start: "tutorial_start",
  step_complete: "step_complete",
  tutorial_finish: "tutorial_finish",
  tutorial_skip: "tutorial_skip",
  step_skipped_by_role: "step_skipped_by_role",
};

export interface EmitGa4Options {
  // Optional measurement id override. When omitted, callers are expected
  // to have called `ReactGA.initialize(...)` themselves (staff-app does
  // this in hooks/usePageTracking.ts with G-12TYHW4V9B).
  measurementId?: string;
  // If false, disables sending entirely. Handy for tests / demos.
  enabled?: boolean;
}

// Build a TutorialEventHandler that forwards events to GA4 via react-ga4.
// Spec v2.1 訂正 (#128): GTM dataLayer.push ではなく react-ga4 pattern extend
// で emit する (staff-app は react-ga4 直接統合済・G-12TYHW4V9B)。
export function createGa4EventEmitter(
  options: EmitGa4Options = {}
): TutorialEventHandler {
  const { measurementId, enabled = true } = options;

  if (measurementId) {
    ReactGA.initialize(measurementId);
  }

  return (payload: TutorialEventPayload) => {
    if (!enabled) return;
    const action = ACTION_BY_EVENT[payload.event];
    if (!action) return;

    ReactGA.event({
      category: TUTORIAL_GA4_CATEGORY,
      action,
      label: payload.stepId,
      // GA4 event params ride via non-standard fields; react-ga4 forwards
      // them as `event_params` on the gtag call.
      ...({
        step_index: payload.stepIndex,
        total_steps: payload.totalSteps,
        required_role: payload.requiredRole,
        current_roles: payload.currentRoles?.join(","),
        timestamp: payload.timestamp,
      } as unknown as Record<string, string | number | undefined>),
    });
  };
}
